
import {decode} from 'html-entities';
import searchRESTApi from '../utils/searchRESTApi';
import {debounce} from 'throttle-debounce'
import NewTabIcon from './NewTabIcon';

const React = window.React || null;
const { useEffect, useCallback } = React;

type SearchProps = {
    onClose?: () => void;
};

type SearchResult = {
    id: number;
    title: string;
    url: string;
};

type SearchResultsProps = {
    refSearchEl: React.RefObject<HTMLInputElement | null>;
    refForm: React.RefObject<HTMLFormElement | null>;
    refGlow: React.RefObject<HTMLDivElement | null>;
};

type UnstyledListProps = React.PropsWithChildren<React.HTMLAttributes<HTMLUListElement>> & { ref?: React.Ref<HTMLUListElement> };

export default function Search( {onClose}: SearchProps ): React.ReactElement {

    const refSearchEl = React.useRef<HTMLInputElement>(null);
    const refForm = React.useRef<HTMLFormElement>(null);
    const refGlow = React.useRef<HTMLDivElement>(null);

    const handleSubmit = useCallback((event: React.FormEvent) => {
        event.preventDefault();
        const formData = new FormData(event.target as HTMLFormElement);
        const searchTerm = formData.get('s') as string;
        
        if (searchTerm) {
            const searchUrl = `/?s=${encodeURIComponent(searchTerm)}`;
            window.open(searchUrl, '_blank');
        }
        
        onClose?.();
    }, [onClose]);

    const SearchInput = React.memo( () => {
        
        useEffect(() => {
            if( typeof anime === 'function' && refSearchEl.current ){
                anime({
                    targets: refSearchEl.current,
                    opacity: [0, 1],
                    duration: 300,
                    easing: 'easeInOutQuad',
                    delay: 100,
                });
            }
        }, []);
        return (
            <input type="search" placeholder="Search..." aria-label="Search"
                className="form-control form-control border-0 rounded-0 px-3 rounded-pill adw-search"
                ref={refSearchEl}
                name="s"
            />
        )
    } );

    return (
        <div className='position-relative'>

            <div ref={refGlow} className="search-form-glow" />

            {/* Search Input with Icon */}
            <form
                onSubmit={handleSubmit}
                className="d-flex rounded-pill border gap-2 py-2 ps-2 pe-3 z-1 position-relative align-items-center"
                method="GET"
                action="/"
                target="_blank"
                ref={refForm}
                style={{backgroundColor: 'var(--bs-light-bg-subtle)'}}
            >

                {/* Search Input */}
                <div className="flex-grow-1">
                    <label htmlFor="s" className="visually-hidden">Search</label>
                    <SearchInput />
                </div>
                

                {/* Search Icon SVG */}
                <button
                    className="btn btn-link border-0 p-0 text-hover-primary"
                    type='submit'
                    
                >
                    <SearchIcon />
                </button>

            </form>

            <SearchResults refSearchEl={refSearchEl} refForm={refForm} refGlow={refGlow} />

        </div>
    );
}

const SearchIcon = ( props : any ): React.ReactElement => {
    return (
        <svg {...props} style={{transform: 'rotate(-18deg)'}} width="33" height="32" viewBox="0 0 33 32" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.6162 26.7015L16.1375 19.4884C14.8806 19.7553 13.5371 19.6991 12.2382 19.2424C8.57158 17.9535 6.67425 14.0194 7.99991 10.4544C9.32557 6.88943 13.3718 5.04468 17.0384 6.3336C20.705 7.62253 22.6024 11.5566 21.2767 15.1216C20.8071 16.3845 19.9914 17.4253 18.9776 18.1936L22.4563 25.4066L19.6162 26.7015ZM12.7715 17.8081C15.6179 18.8087 18.7721 17.3713 19.8015 14.603C20.8309 11.8348 19.3514 8.76851 16.5051 7.76792C13.6587 6.76732 10.5045 8.20477 9.47511 10.973C8.44572 13.7412 9.92515 16.8075 12.7715 17.8081Z" />
        </svg>
    )
}

const SearchResults = ( {refSearchEl, refForm, refGlow}: SearchResultsProps ): React.ReactElement => {

    const refPlaceholderList = React.useRef<HTMLUListElement>(null);

    const [searchResults, setSearchResults] = React.useState<SearchResult[]>([]);
    const [resultsTotalPages, setResultsTotalPages] = React.useState(0);
    const [isSearching, setIsSearching] = React.useState(false);

    const [searchTerm, setSearchTerm] = React.useState(''); // Search Term
    const abortController = new AbortController();

    function handleSearchChange( event: Event ){
        const target = event.target as HTMLInputElement;
        setSearchTerm( target.value );
    }

    useEffect(() => {
        async function fetchSearchResults() {
            try {
                setIsSearching(true);
                const res = await searchRESTApi({ searchString: searchTerm, signal: abortController.signal });
                if (res && Array.isArray(res.results)) {
                    setSearchResults(res.results);
                    setResultsTotalPages(Number(res.totalPages || 0));
                } else {
                    setSearchResults([]);
                    setResultsTotalPages(0);
                }
            } catch (error) {
                console.error(error);
                setSearchResults([]);
                setResultsTotalPages(0);
            } finally {
                setIsSearching(false);
            }
        }

        if (searchTerm.length >= 3) {

            const debounceSearch = debounce(400, () => {
                fetchSearchResults();
            });
            debounceSearch();
        }

        return () => {
            abortController.abort();
        };
    }, [searchTerm]);

    useEffect(() => {

        refSearchEl.current?.addEventListener('input', handleSearchChange, {
            passive: true
        });
        return () => {
            refSearchEl.current?.removeEventListener('input', handleSearchChange);
        }
    }, []);

    const clearSearch = () => {
        setSearchTerm('');
        setSearchResults([]);
        setResultsTotalPages(0);
        if( refSearchEl.current ){
            refSearchEl.current.value = '';
        }
    }

    useEffect(() => {

        if( searchTerm?.length > 0 && isSearching ){
            refForm.current?.classList.add('border-animate');
            refGlow.current?.classList.add('is-active');
        } else {
            refForm.current?.classList.remove('border-animate');
            refGlow.current?.classList.remove('is-active');
        }

    }, [refForm, refGlow, searchTerm, isSearching] )

    // Animate list item widths using transform
    useEffect(() => {
        if( refPlaceholderList.current ){
            const listItems = refPlaceholderList.current.querySelectorAll('.placeholder');
            listItems.forEach( (item: Element) => {
                // Use anime with reverse true to animate from width random width to original width using transform and scaleX
                if( 'function' === typeof anime ){
                    anime.set(item, { transformOrigin: 'left center' });
                    anime({
                        targets: item,
                        scaleX: [Math.random() + 0.5, 1],
                        duration: 5000,
                        easing: 'easeInOutQuad',
                        delay: anime.stagger(200),
                        loop: true,
                        direction: 'alternate',
                    });
                }
            });
         }
     }, [isSearching, refPlaceholderList] )

    const UnstyledList = ( {children, ref, ...props}: UnstyledListProps ) => <ul {...props} ref={ref} className="list-unstyled mb-0 d-flex flex-column gap-3 gap-lg-4 my-lg-4">{children}</ul>;
    return (
        <div className='position-relative'>
            {
                searchTerm?.length > 0 &&
                <div className="position-absolute z-1 w-100 px-3 px-lg-4 py-3 mt-4 border rounded-4"
                style={{
                    overflowY: 'auto',
                    backgroundColor: 'var(--bs-body-bg)',
                    boxShadow: 'rgba(0, 0, 0, 0.15) 0em 20px 32px 5px',
                }}>
                    
                    <div className="placeholder-glow" role="status">
                    { isSearching && 

                        <>
                        <div className='mb-2'>
                            Searching...
                        </div>
                        <UnstyledList ref={refPlaceholderList} style={{maxWidth: '800px'}}>
                            <li className="placeholder my-2 col-6"></li>
                            <li className="placeholder my-2 col-10"></li>
                            <li className="placeholder my-2 col-6"></li>
                            <li className="placeholder my-2 col-9"></li>
                            <li className="placeholder my-2 col-8"></li>
                            <li className="placeholder my-2 col-11"></li>
                        </UnstyledList>
                        </>
                    }
                    </div>
                    {/* Results */}
                    { searchResults.length === 0 && !isSearching && <p className="my-4 text-primary fw-semibold">No results found.</p> }
                    { searchResults?.length > 0 && !isSearching &&
                        <>
                            <div className="d-flex align-items-center justify-content-between pb-2 mb-3 border-bottom fw-semibold">
                                <p className='d-flex mb-0'>Showing results for: <span className='fw-bold text-primary'>&nbsp;{searchTerm}</span></p>
                                <a href="#" className="opacity-75 fs-7" onClick={clearSearch}>Clear Search</a>
                            </div>
                            <div 
                            >
                                <UnstyledList>
                                    {searchResults.map( (result: SearchResult) => {
                                        const titleStr = decode(result.title);
                                        return(
                                            <li key={result.id}
                                                className=""
                                            >
                                                <a className="d-block text-decoration-none text-hover-primary" href={result.url}>{titleStr}</a>
                                            </li>
                                        )
                                    })}
                                </UnstyledList>

                                { resultsTotalPages > 1 && (
                                    <div className="mt-3 mx-n3 mx-lg-n4 mb-n3">
                                        <a
                                            className="btn btn-link d-flex rounded-0 justify-content-center border-top w-100 text-center align-items-center gap-1"
                                            href={`/?s=${encodeURIComponent(searchTerm)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <span>View All</span>
                                            <NewTabIcon className="fs-8" />
                                        </a>
                                    </div>
                                ) }
                            </div>
                        </>
                    }
                </div>
            }


        </div>
    )
}